import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SEOHead from '@/components/base/SEOHead';
import Breadcrumb from '@/components/base/Breadcrumb';
import ArticleCard from '@/components/base/ArticleCard';
import { getArticleById, articles } from '@/mocks/articles';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const article = id ? getArticleById(id) : undefined;

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <h1 className="font-heading text-2xl font-semibold text-foreground-800">文章未找到</h1>
            <p className="mt-2 text-foreground-500">你访问的文章不存在或已被删除</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  const relatedArticles = articles
    .filter((a) => a.id !== article.id && a.categorySlug === article.categorySlug)
    .slice(0, 3);

  const articleJsonLd = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: 'https://qinglanriyu.cn' },
          { '@type': 'ListItem', position: 2, name: article.category, item: `https://qinglanriyu.cn/category/${article.categorySlug}` },
          { '@type': 'ListItem', position: 3, name: article.title, item: `https://qinglanriyu.cn/article/${article.id}` },
        ],
      },
      {
        '@type': 'Article',
        headline: article.title,
        description: article.excerpt,
        image: article.imageUrl,
        datePublished: article.date,
        author: { '@type': 'Person', name: article.author },
        publisher: {
          '@type': 'Organization',
          name: '校园春色',
        },
        mainEntityOfPage: {
          '@type': 'WebPage',
          '@id': `https://qinglanriyu.cn/article/${article.id}`,
        },
      },
    ],
  };

  return (
    <div className="min-h-screen bg-background-50 font-body">
      <SEOHead
        title={`${article.title} - 校园春色`}
        description={article.excerpt}
        keywords={`校园春色,${article.tags.join(',')},青春校园生活,校园美好瞬间`}
        canonicalUrl={`/article/${article.id}`}
        ogImage={article.imageUrl}
        ogType="article"
        jsonLd={articleJsonLd}
      />

      <Navbar />

      <main className="pt-20">
        <Breadcrumb
          items={[
            { label: '首页', href: '/' },
            { label: article.category, href: `/category/${article.categorySlug}` },
            { label: article.title },
          ]}
        />

        <article className="w-full px-6 md:px-10 py-8 max-w-4xl mx-auto">
          <header className="mb-10">
            <div className="flex items-center gap-3 mb-4">
              <Link
                to={`/category/${article.categorySlug}`}
                className="px-3 py-1 text-xs font-medium rounded-full bg-accent-100 text-accent-700 hover:bg-accent-200 cursor-pointer whitespace-nowrap transition-colors"
              >
                {article.category}
              </Link>
              <span className="text-xs text-foreground-400">{article.readTime}</span>
            </div>

            <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl font-semibold text-foreground-900 leading-tight">
              {article.title}
            </h1>

            <div className="flex items-center gap-4 mt-6 text-sm text-foreground-500">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center">
                  <i className="ri-user-line text-primary-600 text-sm"></i>
                </div>
                <span>{article.author}</span>
              </div>
              <span className="text-foreground-300">|</span>
              <div className="flex items-center gap-1.5">
                <i className="ri-calendar-line text-sm"></i>
                <span>{article.date}</span>
              </div>
            </div>
          </header>

          <div className="w-full rounded-lg overflow-hidden mb-10">
            <img
              src={article.imageUrl}
              alt={article.imageAlt}
              className="w-full h-auto object-cover"
            />
          </div>

          <div
            className="article-content max-w-none font-body"
            dangerouslySetInnerHTML={{ __html: article.contentHtml }}
          />

          <div className="mt-10 pt-8 border-t border-background-200/70">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm text-foreground-500 mr-2">标签：</span>
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-3 py-1 text-xs rounded-full bg-secondary-100 text-secondary-700 whitespace-nowrap"
                >
                  {tag}
                </span>
              ))}
            </div>
          </div>
        </article>

        {relatedArticles.length > 0 && (
          <section className="w-full px-6 md:px-10 py-16 bg-background-100">
            <div className="max-w-6xl mx-auto">
              <h2 className="font-heading text-xl font-semibold text-foreground-800 mb-8">
                相关推荐
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {relatedArticles.map((a) => (
                  <ArticleCard key={a.id} article={a} variant="default" />
                ))}
              </div>
            </div>
          </section>
        )}
      </main>

      <Footer />
      <ScrollToTop />
    </div>
  );
}